import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Icons } from "../../../Everou/_assets";
import { Selectors } from "../../../Everou/_reducers/app.reducer";
import { SimpleToggle, CustomAccordion } from "../../../Everou/_components";
import { WaitSpinner } from "../../../Everou/_components/WaitSpinner";
import { CapsuleButton } from "../../../Common/_components";
import { ValidatedTextInput } from "../../../Common/_components/ValidatedTextInput";
import { useGlobalNotification } from "../../../Common/_hooks/useGlobalNotification";
import { PMSLocationsActions } from "../../_actions/PMSLocationsActions";
import { PMSLocationsService } from "../../_services/PMSLocationsService";
import {
    SettingInheritanceStatus,
    SettingsRow,
} from "./PMSLocationSettingsFields";

// Same origin check the backend enforces: scheme://host with an optional `*.`
// wildcard and an optional port. No path, no spaces, no other schemes.
const ORIGIN_RE = /^https?:\/\/(\*\.)?([A-Za-z0-9-]+\.)*[A-Za-z0-9-]+(:\d{1,5})?$/;
const MAX_DOMAINS = 50;
const DEVELOPMENT_INVITATION_ORIGIN = "https://share-dev.baintex.com";
const PRODUCTION_INVITATION_ORIGIN = "https://share.baintex.com";

// Public legal pages and invitation links are served by the same `share`
// host. Its runtime URL already distinguishes development from production,
// unlike the panel's own origin (which can be rental.localhost, for example).
function getInvitationOrigin() {
    try {
        return new URL(window._env_.PRIVACY_POLICY_URL).origin;
    } catch (_) {
        return process.env.NODE_ENV === "production"
            ? PRODUCTION_INVITATION_ORIGIN
            : DEVELOPMENT_INVITATION_ORIGIN;
    }
}

export function PMSLocationEmbedDomains({
    organizationUid = null,
    settings = null,
    initialDomains = null,
    onUpdateOrganization = null,
} = {}) {

    // LIBRARIES
    const { t } = useTranslation();
    const { notify } = useGlobalNotification();
    const dispatch = useDispatch();

    // GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);
    const scopeUid = organizationUid || locationUid;
    const scopeSettings = settings || locationInfo;
    const domainService = PMSLocationsService;

    // LOCAL STATE
    // `domains` and `open` live in one object because they must update in the
    // same render: React 17 doesn't batch setState inside async callbacks, so
    // two separate setters would remount the accordion (key flips to "loaded")
    // before `open` is set, leaving it collapsed even when domains exist.
    // `domains === null` means the initial list hasn't loaded yet.
    const [{ domains, open }, setListState] = useState({
        // Organization info already carries its inherited-domain template.
        // Do not call the location endpoint just to enter this screen: older
        // backends interpret organization_uid there as a missing loc_uid and
        // answer with “no permission over this location”.
        domains: organizationUid ? (initialDomains || []) : null,
        open: false,
    });
    const [newDomain, setNewDomain] = useState("");
    const [newNote, setNewNote] = useState("");
    const [isNewDomainValid, setIsNewDomainValid] = useState(false);
    const [busy, setBusy] = useState(false);
    // Mirrored locally so the toggles animate on click instead of waiting for
    // the location to come back from the server, like the rest of the link
    // settings. The effects below re-sync when it does (or when it fails).
    const [hideHeaderLocal, setHideHeaderLocal] = useState(scopeSettings.invitationLinkHideHeader);
    const [hideFooterLocal, setHideFooterLocal] = useState(scopeSettings.invitationLinkHideFooter);

    useEffect(() => {
        setHideHeaderLocal(scopeSettings.invitationLinkHideHeader);
    }, [scopeSettings.invitationLinkHideHeader]);

    useEffect(() => {
        setHideFooterLocal(scopeSettings.invitationLinkHideFooter);
    }, [scopeSettings.invitationLinkHideFooter]);

    function notifyError(error) {
        const code = Number(error?.name);
        if (code === 201)
            notify(t("mod_pms_location_embed_domains_no_permission"), "error");
        else if (code === 101)
            notify(t("mod_pms_location_embed_domains_invalid"), "error");
        else
            notify(error?.message || t("global_error"), "error");
    }

    // Refresh the list after a mutation. Leaves the open/closed state untouched.
    async function reload() {
        if (organizationUid) return;
        try {
            const res = await domainService.listEmbedDomains(scopeUid);
            setListState(prev => ({ ...prev, domains: Array.isArray(res) ? res : [] }));
        } catch (error) {
            notifyError(error);
        }
    }

    // INITIALIZATION — load once per location and keep this long, advanced
    // section collapsed even when domains are already configured. The count in
    // its header still makes that configuration visible. Depends only on
    // locationUid (not on `notify`/`t`, which change identity every render) so
    // typing in the inputs never refetches.
    // La organizacion se re-lee entera tras cada cambio de ajustes, y el array
    // llega con identidad nueva aunque los dominios sean los mismos: se compara
    // el contenido, o tocar cualquier otro ajuste cierra esta seccion.
    const initialDomainsKey = JSON.stringify(initialDomains || []);

    useEffect(() => {
        let cancelled = false;
        if (organizationUid) {
            // Solo la lista: `open` es del usuario, no del servidor.
            setListState(prev => ({ ...prev, domains: initialDomains || [] }));
            return;
        }
        setListState({ domains: null, open: false });
        if (!scopeUid) {
            setListState({ domains: [], open: false });
            return;
        }
        (async () => {
            try {
                const res = await domainService.listEmbedDomains(scopeUid);
                if (cancelled) return;
                const loaded = Array.isArray(res) ? res : [];
                setListState({ domains: loaded, open: false });
            } catch (error) {
                if (cancelled) return;
                setListState({ domains: [], open: false });
                notifyError(error);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeUid, organizationUid, initialDomainsKey]);

    const list = domains || [];
    const atLimit = list.length >= MAX_DOMAINS;

    const domainWarning = useCallback(
        (value) => (value && !ORIGIN_RE.test(value)
            ? t("mod_pms_location_embed_domains_invalid")
            : undefined),
        [t],
    );

    async function handleAdd() {
        if (busy || !isNewDomainValid || !newDomain) return;
        if (atLimit) {
            notify(t("mod_pms_location_embed_domains_limit"), "error");
            return;
        }
        setBusy(true);
        try {
            if (organizationUid) {
                const next = [
                    ...list,
                    {
                        id: -Date.now(),
                        domain: newDomain.trim(),
                        note: newNote.trim(),
                        is_active: true,
                    },
                ];
                setListState(prev => ({ ...prev, domains: next }));
                setNewDomain("");
                setNewNote("");
                await onUpdateOrganization({
                    embed_domains: organizationDomainsPayload(next),
                });
                return;
            }
            await domainService.createEmbedDomain(scopeUid, newDomain.trim(), newNote.trim());
            setNewDomain("");
            setNewNote("");
            await reload();
        } catch (error) {
            notifyError(error);
        } finally {
            setBusy(false);
        }
    }

    async function handleToggle(domain) {
        try {
            if (organizationUid) {
                const next = list.map(item => item.id === domain.id
                    ? { ...item, is_active: !item.is_active }
                    : item,
                );
                setListState(prev => ({ ...prev, domains: next }));
                await onUpdateOrganization({
                    embed_domains: organizationDomainsPayload(next),
                });
                return;
            }
            await domainService.updateEmbedDomain(scopeUid, domain.id, { is_active: !domain.is_active });
            await reload();
        } catch (error) {
            notifyError(error);
        }
    }

    async function handleSaveNote(domain, note) {
        if ((note || "") === (domain.note || "")) return;
        try {
            if (organizationUid) {
                const next = list.map(item => item.id === domain.id
                    ? { ...item, note }
                    : item,
                );
                setListState(prev => ({ ...prev, domains: next }));
                await onUpdateOrganization({
                    embed_domains: organizationDomainsPayload(next),
                });
                return;
            }
            await domainService.updateEmbedDomain(scopeUid, domain.id, { note });
            await reload();
        } catch (error) {
            notifyError(error);
        }
    }

    function handleChangeHideHeader(value) {
        setHideHeaderLocal(value);
        if (organizationUid) {
            onUpdateOrganization({ invitation_link_hide_header: value });
        } else {
            dispatch(PMSLocationsActions.updateInvitationLinkHideHeader(value, locationUid));
        }
    }

    function handleChangeHideFooter(value) {
        setHideFooterLocal(value);
        if (organizationUid) {
            onUpdateOrganization({ invitation_link_hide_footer: value });
        } else {
            dispatch(PMSLocationsActions.updateInvitationLinkHideFooter(value, locationUid));
        }
    }

    async function handleDelete(domain) {
        try {
            if (organizationUid) {
                const next = list.filter(item => item.id !== domain.id);
                setListState(prev => ({ ...prev, domains: next }));
                await onUpdateOrganization({
                    embed_domains: organizationDomainsPayload(next),
                });
                return;
            }
            await domainService.deleteEmbedDomain(scopeUid, domain.id);
            await reload();
        } catch (error) {
            notifyError(error);
        }
    }

    // The arrow sits right after the title, not pushed to the far end of the
    // row: across a wide screen it read as an unrelated icon and the section
    // didn't look expandable at all.
    const header = (
        <div className="c-new-ft-l text-uppercase font-weight-bold d-flex align-items-center cp py-1">
            <img
                alt="arrow"
                src={Icons.arrowDown}
                // Closed points right (into the section), open points down.
                // Rotating the down arrow the other way (+90°) pointed it left,
                // back out of the row.
                className={`c-transition mr-2 ${!open && "c-rotate-ccw-90"}`}
            />
            <span>{t("mod_pms_location_embed_domains_title")}</span>
            {list.length > 0 &&
                <span className="text-secondary ml-2">
                    ({list.length})
                </span>
            }
        </div>
    );

    const body = domains === null
        ? <div className="py-3 d-flex justify-content-center"><WaitSpinner /></div>
        : (
            <div className="mt-2">
                <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-2">
                    <img
                        src={Icons.info}
                        alt="info"
                        className="mr-2 flex-shrink-0"
                        style={{ width: "24px", height: "24px" }}
                    />
                    <span>{t("mod_pms_location_embed_domains_help")}</span>
                </div>

                {/* Embedded in the host's own site, the link repeats what the page
                    around it already says (name, dates) and the icon bar leads out
                    of it, so each block can be dropped on its own. */}
                <SettingsRow
                    label={t("mod_pms_location_embed_hide_header_title")}
                    tooltip={t("mod_pms_location_embed_hide_header_tooltip")}
                >
                    <div>
                        <SimpleToggle
                            className="c-toggle-smooth"
                            checked={hideHeaderLocal}
                            handleChange={({ detail: { value } }) => handleChangeHideHeader(value)}
                        />
                        {!organizationUid && (
                            <SettingInheritanceStatus
                                ownSettings={locationInfo.ownSettings}
                                field="invitationLinkHideHeader"
                                organizationName={locationInfo.settingsOrganizationName}
                                onReset={() => dispatch(
                                    PMSLocationsActions.clearOrganizationSettingOverride(
                                        "invitation_link_hide_header",
                                        locationUid,
                                    ),
                                )}
                            />
                        )}
                    </div>
                </SettingsRow>
                <SettingsRow
                    label={t("mod_pms_location_embed_hide_footer_title")}
                    tooltip={t("mod_pms_location_embed_hide_footer_tooltip")}
                >
                    <div>
                        <SimpleToggle
                            className="c-toggle-smooth"
                            checked={hideFooterLocal}
                            handleChange={({ detail: { value } }) => handleChangeHideFooter(value)}
                        />
                        {!organizationUid && (
                            <SettingInheritanceStatus
                                ownSettings={locationInfo.ownSettings}
                                field="invitationLinkHideFooter"
                                organizationName={locationInfo.settingsOrganizationName}
                                onReset={() => dispatch(
                                    PMSLocationsActions.clearOrganizationSettingOverride(
                                        "invitation_link_hide_footer",
                                        locationUid,
                                    ),
                                )}
                            />
                        )}
                    </div>
                </SettingsRow>
                <div className="mb-3" />

                {/* Add domain */}
                <div className="d-flex align-items-start mb-3" style={{ columnGap: "8px" }}>
                    <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <ValidatedTextInput
                            value={newDomain}
                            setValue={setNewDomain}
                            setIsValid={setIsNewDomainValid}
                            warningFns={[domainWarning]}
                            placeholder={t("mod_pms_location_embed_domains_add_placeholder")}
                        />
                    </div>
                    <div style={{ flex: "1 1 0", minWidth: 0 }}>
                        <ValidatedTextInput
                            value={newNote}
                            setValue={setNewNote}
                            placeholder={t("mod_pms_location_embed_domains_note_placeholder")}
                        />
                    </div>
                    <CapsuleButton
                        text={t("mod_pms_location_embed_domains_add_button")}
                        icon={Icons.add}
                        onClick={handleAdd}
                        isEnabled={!busy && isNewDomainValid && !!newDomain && !atLimit}
                        size={"SMALL"}
                    />
                </div>

                {/* Domain list */}
                {list.length === 0
                    ? <div className="text-secondary mb-3">{t("mod_pms_location_embed_domains_empty")}</div>
                    : (
                        <div className="border c-rounded-big bg-white mb-3">
                            {list.map((domain) => (
                                <EmbedDomainRow
                                    key={domain.id}
                                    domain={domain}
                                    onToggle={() => handleToggle(domain)}
                                    onSaveNote={(note) => handleSaveNote(domain, note)}
                                    onDelete={() => handleDelete(domain)}
                                    notePlaceholder={t("mod_pms_location_embed_domains_note_placeholder")}
                                    deleteTooltip={t("global_delete")}
                                />
                            ))}
                        </div>
                    )
                }

                {/* Embed snippet for the partner */}
                <EmbedSnippet />
            </div>
        );

    // Remount when the initial load finishes so the accordion picks up the right
    // default open state (CustomAccordion only reads `defaultOpened` on mount).
    return (
        <CustomAccordion
            key={domains === null ? "loading" : "loaded"}
            defaultOpened={domains !== null && open}
            onClick={() => setListState(prev => ({ ...prev, open: !prev.open }))}
            header={header}
            body={body}
        />
    );
}

// The organization template is updated as one value. New rows have a local
// negative id only so React can render them immediately; the backend assigns
// the persistent id and returns it in organization/info.
function organizationDomainsPayload(domains) {
    return domains.map(({ id, domain, note, is_active }) => ({
        ...(id > 0 ? { id } : {}),
        domain,
        note,
        is_active,
    }));
}

function EmbedDomainRow({ domain, onToggle, onSaveNote, onDelete, notePlaceholder, deleteTooltip }) {

    const [note, setNote] = useState(domain.note || "");

    useEffect(() => { setNote(domain.note || ""); }, [domain.note]);

    return (
        <div
            className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
            style={{ columnGap: "12px" }}
        >
            <span className="text-truncate" style={{ flex: "1 1 0", minWidth: 0 }} title={domain.domain}>
                {domain.domain}
            </span>
            <input
                className="form-control form-control-sm"
                style={{ flex: "1 1 0", minWidth: 0 }}
                value={note}
                placeholder={notePlaceholder}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => onSaveNote(note)}
            />
            <SimpleToggle
                className="c-toggle-smooth"
                checked={domain.is_active}
                handleChange={onToggle}
            />
            <CapsuleButton
                onClick={onDelete}
                // eslint-disable-next-line react/style-prop-object
                style="DANGER"
                size="SMALL"
                icon={Icons.trash}
                tooltip={deleteTooltip}
            />
        </div>
    );
}

function EmbedSnippet() {

    const { t } = useTranslation();
    const { notify } = useGlobalNotification();

    // The partner pastes this on their own site. The public link authenticates
    // by its own `code`, so it works cross-domain without any cookie/session.
    const invitationOrigin = getInvitationOrigin();
    const snippet =
`<iframe
  id="ai360-invitation"
  src="${invitationOrigin}/public/invitation/<code>?embed=1"
  title="AI360 access link"
  style="width:100%; height:400px; min-height:120px; border:0;"
  allow="geolocation"
></iframe>

<!-- Optional: automatically adjust the iframe height to its content. -->
<script>
  const ai360Invitation = document.getElementById("ai360-invitation");

  window.addEventListener("message", function (event) {
    if (
      event.origin !== "${invitationOrigin}" ||
      event.source !== ai360Invitation.contentWindow ||
      event.data?.type !== "baintex:invitation:resize"
    ) {
      return;
    }

    const height = Number(event.data.height);

    if (Number.isFinite(height) && height > 0) {
      ai360Invitation.style.height = Math.ceil(height) + "px";
    }
  });
</script>`;

    const handleCopy = () => {
        navigator.clipboard.writeText(snippet);
        notify(t("global_copied"), "success");
    };

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between mb-1">
                <span className="text-secondary">{t("mod_pms_location_embed_domains_snippet_title")}</span>
                <CapsuleButton
                    text={t("global_copy")}
                    icon={Icons.copy}
                    onClick={handleCopy}
                    size={"SMALL"}
                />
            </div>
            <div className="text-secondary mb-2">
                {t("mod_pms_location_embed_domains_snippet_help")}
            </div>
            <pre
                className="c-bg-grey-250 c-rounded-big p-3 mb-0"
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
            >
                <code>{snippet}</code>
            </pre>
        </div>
    );
}
